import React, { useEffect, useCallback } from 'react';
import {
  Control, Resolver, useForm, useWatch,
} from 'react-hook-form';
import {
  TextField,
  Divider,
  InputAdornment,
  TextFieldProps,
  Box,
} from '@mui/material';
import { Text } from '@bighealth/react-limbix-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { GraphQLFormattedError } from 'graphql';

import Styled from './EditPatient.styles';

import { PdtType } from '@/apollo/__generated__/graphql';

import ErrorMessage from '@/components/ErrorMessage';
import { EditPatientFields, EditPatientFormType } from '@/types';
import { DATE_FORMAT_REGEXP, VALID_DATE_REGEXP } from '@/utils/constants';
import { useModal } from '@/hooks/redux';

type SubmitButtonProps = {
  control: Control<EditPatientFormType>,
  isDirty: boolean,
}
function SubmitButton(props: SubmitButtonProps) {
  const { control, isDirty } = props;
  const formData = useWatch({
    control,
  });
  // Check all required fields are non-null (filled)
  const requiredFieldsFilled = Object.entries(formData).every(([key, value]) => (
    key === 'phone' || !!value
  ));

  return (
    <Styled.SubmitButton
      type="submit"
      fullWidth
      variant="contained"
      color="primary"
      disabled={!requiredFieldsFilled || !isDirty}
    >
      Save Changes
    </Styled.SubmitButton>
  );
}

// Client-side validation using Yup for the form for easier processing and error handling
const nameValidation = string()
  .matches(/^[a-zA-Z\s'-]+$/, 'Please enter a valid name')
  .max(255, 'Please enter a string with fewer than 255 characters');
const validationSchema = object().shape({
  firstName: nameValidation.required(''),
  lastName: nameValidation.required(''),
  dateOfBirth: string().required('')
    .matches(DATE_FORMAT_REGEXP, 'Please only enter numbers in the MM/DD/YYYY format')
    .matches(VALID_DATE_REGEXP, 'Please enter a valid date'),
  email: string()
    .required('')
    .email('Please enter a valid email address')
    .max(255, 'Please enter a valid email address with less than 255 characters')
    .trim(),
  phone: string().optional(),
  pdtUid: string().optional(),
});

const ERRORS = {
  EXISTING_EMAIL_ERROR: 'email already exists',
  INVALID_EMAIL_ERROR: 'valid email address',
  PATIENT_AGE_ERROR: 'under the age of',
  PHONE_ERROR: 'phone',
};

type FormFieldProps = TextFieldProps & {
  errorMessage: string,
};
const FormField = React.forwardRef<HTMLInputElement, FormFieldProps>((props, ref) => {
  const { errorMessage, ...rest } = props;
  return (
    <Box margin="22px 0px">
      <TextField
        variant="outlined"
        size="small"
        margin="none"
        type="text"
        fullWidth
        autoCapitalize="none"
        InputLabelProps={{ shrink: true }}
        {...rest}
        ref={ref}
      />
      <ErrorMessage message={errorMessage} placeholderHeight="10px" fontSize="12px" />
    </Box>
  );
});

type Props = {
  onSubmit: (formData: EditPatientFormType) => Promise<void> | void;
  submitSuccess: boolean;
  errors: readonly GraphQLFormattedError[] | undefined;
  pdts: PdtType[];
  defaultValues: EditPatientFormType;
  onDirtyChange?: (dirty: boolean) => void;
};

function EditPatientForm(props : Props) {
  const {
    onSubmit,
    submitSuccess,
    errors: mutationErrors,
    pdts,
    defaultValues,
    onDirtyChange,
  } = props;

  const { showModal, hideModal } = useModal();
  const {
    register,
    handleSubmit,
    control,
    reset,
    setFocus,
    setError,
    formState: {
      errors,
      isDirty,
    },
  } = useForm<EditPatientFormType>({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema) as Resolver<EditPatientFormType>,
    defaultValues,
  });

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  const handleExistingEmailError = useCallback(() => {
    setError(EditPatientFields.EMAIL, { message: 'Email address already in use' });
    return EditPatientFields.EMAIL;
  }, []);

  const handleInvalidEmailError = useCallback(() => {
    setError(EditPatientFields.EMAIL, { message: 'Please enter a valid email address' });
    return EditPatientFields.EMAIL;
  }, []);

  const handlePatientAgeError = useCallback((errorMessage: string) => {
    setError(EditPatientFields.DATE_OF_BIRTH, { message: errorMessage });
    return EditPatientFields.DATE_OF_BIRTH;
  }, []);

  const handlePatientPhoneError = useCallback(() => {
    setError(EditPatientFields.PHONE, { message: 'Please enter a valid phone number' });
    return EditPatientFields.PHONE;
  }, []);

  const handleUnexpectedError = useCallback(() => {
    showModal('CONFIRMATION_POPUP', {
      header: 'Error saving changes',
      body: `We couldn't save your changes due to an unexpected error.
            Please try again or contact support at clinic@bighealth.com if the issue persists.`,
      confirmButtonText: 'Retry save',
      cancelButtonText: 'Dismiss',
      onConfirm: () => {
        hideModal();
        handleSubmit(onSubmit)();
      },
    });
  }, [showModal, hideModal, handleSubmit, onSubmit]);

  const handleErrorExists = useCallback((errorMessage: string) => {
    const normalizedErrorMessage = errorMessage.toLowerCase();
    if (normalizedErrorMessage.includes(ERRORS.EXISTING_EMAIL_ERROR)) {
      return handleExistingEmailError();
    }
    if (normalizedErrorMessage.includes(ERRORS.INVALID_EMAIL_ERROR)) {
      return handleInvalidEmailError();
    }
    if (normalizedErrorMessage.includes(ERRORS.PATIENT_AGE_ERROR)) {
      return handlePatientAgeError(errorMessage);
    }
    if (normalizedErrorMessage.includes(ERRORS.PHONE_ERROR)) {
      return handlePatientPhoneError();
    }
    return undefined;
  }, [
    handleExistingEmailError,
    handleInvalidEmailError,
    handlePatientAgeError,
    handlePatientPhoneError,
    handleUnexpectedError,
  ]);

  useEffect(() => {
    if (!mutationErrors?.length) {
      return;
    }
    const firstError = mutationErrors[0];
    const errorMessage = firstError?.message;
    if (errorMessage?.length) {
      const focusField = handleErrorExists(errorMessage);
      if (focusField) {
        setTimeout(() => {
          setFocus(focusField);
        }, 100);
      } else {
        handleUnexpectedError();
      }
    } else {
      handleUnexpectedError();
    }
  }, [mutationErrors, handleErrorExists, handleUnexpectedError, setFocus]);

  useEffect(() => {
    if (submitSuccess) {
      reset(defaultValues);
    }
  }, [submitSuccess, defaultValues]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box paddingTop="25px">
        <Text as="h1" fontSize="24px">
          Edit patient details
        </Text>
        <Text color="#4C4D4F" marginBottom="5px">
          Need to make a change? Edit patient details and contact info below.
        </Text>
        <Text as="h2" fontSize="24px" fontWeight="600" marginTop="25px" marginBottom="5px">
          Program
        </Text>
        <FormField
          label="Program"
          value={pdts.find((pdt) => pdt.uid === defaultValues.pdtUid)?.name || 'Unknown Program'}
          InputProps={{
            readOnly: true,
          }}
          errorMessage=""
        />
        <Text as="h2" fontSize="24px" fontWeight="600" marginTop="25px" marginBottom="5px">
          Patient Details
        </Text>
        <FormField
          required
          autoFocus
          label="First name"
          id={EditPatientFields.FIRST_NAME}
          {...register(EditPatientFields.FIRST_NAME)}
          errorMessage={errors?.firstName?.message}
        />
        <FormField
          required
          label="Last name"
          id={EditPatientFields.LAST_NAME}
          {...register(EditPatientFields.LAST_NAME)}
          errorMessage={errors?.lastName?.message}
        />
        <FormField
          required
          autoCapitalize="none"
          label="Date of birth"
          placeholder="MM/DD/YYYY"
          id={EditPatientFields.DATE_OF_BIRTH}
          {...register(EditPatientFields.DATE_OF_BIRTH)}
          errorMessage={errors?.dateOfBirth?.message}
        />
        <Box margin="20px 0px">
          <Divider />
        </Box>
        <Text as="h2" fontSize="24px" fontWeight="600">
          Patient Contact Information
        </Text>
        <Text color="#4C4D4F" fontSize="14px" marginBottom="5px">
          This contact information will be used to send your patient instructions to download and use the program.
        </Text>
        <FormField
          required
          autoCapitalize="none"
          label="Email"
          id={EditPatientFields.EMAIL}
          {...register(EditPatientFields.EMAIL)}
          errorMessage={errors?.email?.message}
        />
        <FormField
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Text fontSize="1rem">
                  🇺🇸
                </Text>
              </InputAdornment>
            ),
          }}
          autoCapitalize="none"
          label="Mobile phone number (optional)"
          id={EditPatientFields.PHONE}
          {...register(EditPatientFields.PHONE)}
          errorMessage={errors?.phone?.message}
        />
        <SubmitButton control={control} isDirty={isDirty} />
      </Box>
    </form>
  );
}

export default EditPatientForm;
